import { Box, Button, IconButton, TextField, Typography } from "@mui/material"
import ContiSpecificButton from "components/ContiSpecificButton"
import { isAte, isMobile } from "utils/Global"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import CloseIcon from "@mui/icons-material/Close"
import BarcodeScannerComponent from "react-qr-barcode-scanner"
import { useTranslation } from "react-i18next"
import { useEffect, useMemo, useState } from "react"
import { ReactComponent as FacingModeIcon } from "../../assets/images/facing_mode.svg"
import { ReactComponent as ZoomOutIcon } from "../../assets/images/zoom_out.svg"
import { ReactComponent as ZoomInIcon } from "../../assets/images/zoom_in.svg"
import { isAndroid, isIOS, isRealMobile } from "utils/util"
import Html5QrcodePlugin from "components/Html5-qrcode/Html5qrcodePlugin"

interface ZoomConstraintSet extends MediaTrackConstraintSet {
  zoom?: number
  focusMode?: string
}
interface ExtendedMediaTrackConstraints extends MediaTrackConstraints {
  zoom?: number
  focusMode: string
  advanced?: ZoomConstraintSet[]
}

export default function Scanner({
  frame,
  title,
  helperText,
  inputLabel,
  showPlaceholder,
  onClickHandle,
  closeScanner,
  setShowInfo,
  productCode,
  handleChange,
  setShowPlaceholder,
  error,
  onUpdate,
  placeholder,
  formatScan,
}) {
  const { t } = useTranslation()
  const [facingMode, setFacingMode] = useState<"environment" | "user">(
    "environment"
  )
  const [zoomLevel, setZoomLevel] = useState(2)
  const [deviceId, setDeviceId] = useState<string>("")
  const smallestZoom = useMemo(() => (isIOS ? 0.5 : 0.6), [])

  useEffect(() => {
    return () => {
      const video = document.querySelector("video")
      if (video && video.srcObject) {
        const stream = video.srcObject as MediaStream
        stream.getTracks().forEach((track) => {
          try {
            track.stop()
          } catch (e) {}
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const changeFacingMode = () => {
    if (facingMode === "environment") {
      setFacingMode("user")
      setZoomLevel(1)
    } else {
      setFacingMode("environment")
      setZoomLevel(2)
    }
  }

  const handleZoomLevel = (type: "out" | "in") => {
    if (type === "out") {
      if (zoomLevel > 1) setZoomLevel(zoomLevel - 1)
      if (zoomLevel === 1 && facingMode === "environment")
        setZoomLevel(smallestZoom)
    }
    if (type === "in" && zoomLevel < 10) {
      if (zoomLevel === smallestZoom) setZoomLevel(1)
      else setZoomLevel(zoomLevel + 1)
    }
  }

  useEffect(() => {
    const timeout = setTimeout(
      () => {
        navigator.mediaDevices &&
          navigator.mediaDevices
            .enumerateDevices()
            .then((devices) => {
              let cameraId
              //check first for 2_0 camera as this is usally the default back one
              for (const device of devices) {
                const cameraLabel = device.label
                if (
                  cameraLabel &&
                  cameraLabel.toLowerCase().includes("back") &&
                  (cameraLabel.toLowerCase().includes("2 0") ||
                    cameraLabel.toLowerCase().includes("2.0"))
                ) {
                  cameraId = device.deviceId
                }
              }
              //if none is found take the first back one
              if (cameraId == null) {
                for (const device of devices) {
                  const cameraLabel = device.label
                  if (
                    cameraLabel &&
                    cameraLabel.toLowerCase().includes("back")
                  ) {
                    cameraId = device.deviceId
                  }
                }
              }
              if (cameraId) setDeviceId(cameraId)
            })
            .catch(console.error)
      },
      isAndroid ? 500 : 10
    )
    return () => clearTimeout(timeout)
  })

  return (
    <Box
      className="scanner-container"
      sx={{
        position: "fixed",
        display: "block",
        zIndex: 9999,
        top: 0,
        bottom: 0,
        right: 0,
        left: 0,
        backgroundColor: "white",
      }}
    >
      {/* SCANNER */}
      {showPlaceholder && (
        <Box
          style={{
            position: "absolute",
            background: "black",
            width: "100%",
            paddingTop: "62px",
            height: "calc(100% - 150px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            overflow: "hidden",
          }}
        >
          <img
            src={frame}
            alt=""
            style={{
              width: 350,
              height: 350,
              display: "block",
              position: "absolute",
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 2,
            }}
          />
          <Box
            style={{ transform: facingMode === "user" ? "scaleX(-1)" : "none" }}
          >
            {isAndroid ? (
              <Html5QrcodePlugin
                fps={10}
                // qrbox={300}
                disableFlip={false}
                qrCodeSuccessCallback={(decodedTextNew, _decodedResult) => {
                  onUpdate("", decodedTextNew)
                }}
              />
            ) : (
              <BarcodeScannerComponent
                key={`${facingMode}-${zoomLevel}-${deviceId}`}
                stopStream={true}
                width="100%"
                height={
                  isRealMobile
                    ? (window.innerWidth * 4) / 3
                    : (window.innerWidth * 16) / 9
                }
                videoConstraints={
                  {
                    facingMode,
                    aspectRatio: isRealMobile ? 4 / 3 : 9 / 16,
                    advanced: [{ autoGainControl: false }, { zoom: zoomLevel }],
                    focusMode: "continuous",
                    deviceId:
                      deviceId &&
                      facingMode === "environment" &&
                      zoomLevel >= 1 &&
                      isAndroid
                        ? { exact: deviceId }
                        : undefined,
                  } as ExtendedMediaTrackConstraints
                }
                onUpdate={onUpdate}
                facingMode={facingMode}
                formats={[formatScan]}
              />
            )}
          </Box>
          {/* Camera Control */}
          {!isAndroid && (
            <Box
              style={{
                bottom: 26,
                padding: 16,
                width: "100%",
                position: "absolute",
                display: "flex",
                alignItems: "stretch",
                justifyContent: "space-between",
              }}
            >
              <Button
                title="Switch camera"
                style={{
                  minWidth: 40,
                  color: "white",
                  background: "#343A40",
                }}
                onClick={changeFacingMode}
              >
                <FacingModeIcon />
              </Button>
              <div
                className="zoom-controls"
                id="zoomControls"
                style={{
                  background: "#343A40",
                  color: "white",
                  borderRadius: 4,
                  gap: 4,
                  display: "flex",
                  alignItems: "stretch",
                  justifyContent: "space-between",
                }}
              >
                <Button
                  style={{
                    minWidth: 40,
                    color: "white",
                  }}
                  onClick={() => handleZoomLevel("out")}
                >
                  <ZoomOutIcon />
                </Button>
                <label
                  id="zoomLevel"
                  style={{
                    padding: "8px 0",
                    textAlign: "center",
                    minWidth: 30,
                    color: "white",
                    margin: 0,
                  }}
                >
                  {zoomLevel}
                </label>
                <Button
                  style={{
                    minWidth: 40,
                    color: "white",
                  }}
                  onClick={() => handleZoomLevel("in")}
                >
                  <ZoomInIcon />
                </Button>
              </div>
            </Box>
          )}
        </Box>
      )}
      {/* TITLE BAR */}
      <Box
        sx={{
          position: "relative",
          backgroundColor: "white",
          padding: "16px",
          fontSize: "20px",
        }}
      >
        {title}
        <IconButton
          aria-label="close"
          onClick={closeScanner}
          sx={{
            width: "48px",
            height: "48px",
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      {/* INPUT AREA */}
      <Box
        sx={{
          transition: ".3s ease all",
          fontSize: "20px",
          width: "100%",
          backdropFilter: "blur(10px)",
          position: showPlaceholder ? "absolute" : "relative",
          bottom: showPlaceholder ? 0 : "auto",
          padding: showPlaceholder ? "16px" : "0 16px",
          height: showPlaceholder ? "auto" : "100%",
          borderRadius: showPlaceholder ? "16px 16px 0 0" : "0",
          background: showPlaceholder
            ? "linear-gradient(0deg,rgba(255, 255, 255, 1) 0%, rgba(245, 245, 245, 1) 50%, rgba(255, 255, 255, 0.8) 100%)"
            : "white",
        }}
      >
        {showPlaceholder && (
          <>
            <Box
              sx={{
                width: "57px",
                height: "4px",
                backgroundColor: "#aaa",
                borderRadius: "4px",
                margin: "0 auto 16px",
              }}
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "16px",
              }}
              onClick={() => setShowInfo(true)}
            >
              <InfoOutlinedIcon
                sx={{ width: "24px", height: "24px" }}
                color="inherit"
              />
              <Typography>{helperText}</Typography>
            </Box>
          </>
        )}

        <Typography sx={{ fontWeight: "bold", marginBottom: "16px" }}>
          {inputLabel}
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <TextField
            sx={{ flex: "1" }}
            fullWidth={!isMobile}
            id="outlined-basic-mapp-code"
            name="mapp-code"
            variant="outlined"
            placeholder={showPlaceholder ? placeholder : ""}
            value={productCode || ""}
            inputProps={{
              min: 0,
              style: { textAlign: "left", textTransform: "uppercase" },
            }}
            InputProps={{
              style: {
                padding: "0",
                margin: "0",
                borderRadius: "30px",
                fontFamily: isAte
                  ? "Etelka Light"
                  : "ContinentalStagSans-Light",
                fontSize: "16px",
                fontStyle: "italic",
                letterSpacing: "3px",
                height: "44px",
                backgroundColor: "#ffff",
              },
            }}
            onChange={handleChange}
            onFocus={() => setShowPlaceholder(false)}
            onBlur={() => {
              if (!productCode) setShowPlaceholder(true)
            }}
          />
          <Box sx={{}}>
            <ContiSpecificButton
              onClick={onClickHandle}
              text={t("mappCodeCard.buttonText")}
              isForwardIcon={true}
              width="100px"
            ></ContiSpecificButton>
          </Box>
        </Box>
        {error && (
          <Typography
            variant={"body2"}
            fontWeight={700}
            color="white"
            fontSize="14px"
            sx={{
              padding: "8px",
              backgroundColor: "#AA182A",
              borderRadius: "8px",
              margin: "16px 0 0",
            }}
          >
            {error}
          </Typography>
        )}
      </Box>
    </Box>
  )
}
